import { useState } from 'react';
import type { ReactNode } from 'react';
import { TenantStatus } from '@yourstore/domain';
import { useApi, useConsole, useRead } from './context';
import { Empty, Failure, Loading, StatusPill, When } from './bits';

/**
 * The fleet: every tenant, one row each.
 *
 * The columns are chosen to answer the four questions an operator actually
 * arrives with, and nothing else:
 *
 *  * **Where is this business in its life?** — status, coloured, because `live`
 *    is the one that makes `authority: "live"` true (spec §4.4) and it should
 *    be findable by eye down a column.
 *  * **What has it bought?** — the module count and the modules themselves,
 *    since a `partner_api` that is off is why an agent's key "does not work".
 *  * **Can anyone reach it?** — active keys and endpoints, with a failing
 *    endpoint called out: a tenant whose deliveries have exhausted their
 *    retries is a partner that has stopped hearing about bookings, and nothing
 *    else on this screen would show it.
 *  * **Is it alive?** — last activity, which is null for a tenant created and
 *    never touched. That null is not a gap in the data; it is exactly the row
 *    an operator mid-onboarding is looking for, so it says "never" rather than
 *    an em dash.
 *
 * What is **not** here: any count of guests, bookings or money. Those are the
 * tenant's book, and this surface holds no grant on it (spec §6.3).
 */
export function Fleet({
  onOpen,
  onCreate,
}: {
  onOpen: (tenantId: string) => void;
  onCreate: () => void;
}): ReactNode {
  const api = useApi();
  const { config } = useConsole();
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);

  const fleet = useRead(
    () => api.fleet({ limit: config.pageSize, offset, status: status === '' ? undefined : status }),
    [status, offset, config.pageSize],
  );

  const page = fleet.value;

  return (
    <div className="view">
      <header className="view__head">
        <h2>Fleet</h2>
        <div className="view__actions">
          <label className="inline-field">
            <span>Status</span>
            <select
              value={status}
              onChange={(e) => {
                setOffset(0);
                setStatus(e.target.value);
              }}
            >
              <option value="">any</option>
              {TenantStatus.options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="button" onClick={fleet.reload} disabled={fleet.loading}>
            Refresh
          </button>
          <button type="button" className="button button--primary" onClick={onCreate}>
            Create tenant
          </button>
        </div>
      </header>

      {fleet.failure !== null ? <Failure failure={fleet.failure} /> : null}
      {page === null ? (
        fleet.loading ? (
          <Loading what="the fleet" />
        ) : null
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Name</th>
                <th>Status</th>
                <th>Modules</th>
                <th>Keys</th>
                <th>Endpoints</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {page.tenants.map((t) => (
                <tr
                  key={t.tenant_id}
                  className="table__row--click"
                  onClick={() => onOpen(t.tenant_id)}
                >
                  <td>
                    <button
                      type="button"
                      className="linkish"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(t.tenant_id);
                      }}
                    >
                      {t.tenant_id}
                    </button>
                  </td>
                  <td>{t.name}</td>
                  <td>
                    <StatusPill status={t.status} />
                  </td>
                  <td>
                    <span className="count">{t.module_count}</span>{' '}
                    <span className="muted">{t.modules.join(' · ')}</span>
                  </td>
                  <td>
                    <span className="count">{t.active_keys}</span>
                    {t.revoked_keys > 0 ? (
                      <span className="muted"> (+{t.revoked_keys} revoked)</span>
                    ) : null}
                  </td>
                  <td>
                    <span className="count">{t.webhook_endpoints}</span>
                    {t.webhook_failing ? <span className="flag flag--bad">failing</span> : null}
                  </td>
                  <td>
                    {t.last_activity_at === null ? (
                      <span className="muted">never</span>
                    ) : (
                      <When iso={t.last_activity_at} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {page.tenants.length === 0 ? (
            <Empty>
              No tenants{status === '' ? '' : ` with status ${status}`}. Create one to begin.
            </Empty>
          ) : null}

          <Pager
            total={page.total}
            count={page.tenants.length}
            limit={page.limit}
            offset={page.offset}
            busy={fleet.loading}
            onOffset={setOffset}
          />
        </>
      )}
    </div>
  );
}

/**
 * Offset paging, off the response rather than the request.
 *
 * The server clamps `limit` silently and echoes what it actually used, so the
 * page controls read what came back — a console that paged by its own
 * arithmetic would skip rows the moment somebody raised `PAGE_SIZE_MAX`.
 *
 * Only the fleet listing carries a `total`; the delivery log and the audit log
 * do not, because counting an append-only table that grows forever is the
 * unbounded read spec §5.5.5 rules out. So the end of the list is inferred the
 * only honest way available — a short page — and `count` is how the caller says
 * how many rows it actually got.
 */
export function Pager({
  total,
  count,
  limit,
  offset,
  busy,
  onOffset,
}: {
  total?: number | undefined;
  count?: number | undefined;
  limit: number;
  offset: number;
  busy: boolean;
  onOffset: (offset: number) => void;
}): ReactNode {
  const atEnd =
    total !== undefined ? offset + limit >= total : count !== undefined && count < limit;
  return (
    <div className="pager">
      <button
        type="button"
        className="button"
        disabled={busy || offset === 0}
        onClick={() => onOffset(Math.max(0, offset - limit))}
      >
        Previous
      </button>
      <span className="muted">
        {offset + 1}–{offset + limit}
        {total === undefined ? '' : ` of ${total}`}
      </span>
      <button
        type="button"
        className="button"
        disabled={busy || atEnd}
        onClick={() => onOffset(offset + limit)}
      >
        Next
      </button>
    </div>
  );
}
